import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import React from 'react'

import { Media } from '@/components/Media'
import { Button } from '@/components/ui/button'
import {
  DEFAULT_POST_HERO_IMAGE,
  getCategoryTitle,
  getPostCardCategoryColor,
  getPostCardDescription,
  getPostCardImage,
  getPostCardReadTime,
  type BlogPostData,
} from '@/lib/blog/postCard'
import { cn } from '@/utilities/ui'

export type BlogArticleCardProps = {
  badgeStyle?: 'blog' | 'event'
  category?: string
  categoryColor: string
  className?: string
  ctaLabel?: string
  description?: string
  href?: string
  image?: BlogPostData['heroImage']
  imageAspectClassName?: string
  isExpired?: boolean
  isExternal?: boolean
  linkDisabled?: boolean
  listItem?: boolean
  opensInNewTabLabel?: string
  readTime: string
  title: string
  variant?: 'default' | 'featured'
}

const DEFAULT_IMAGE_ASPECT_CLASS_NAME = 'aspect-[520/264]'

const EVENT_LOCATION_BADGE_ICON = '/media/homepage/blog/blog-eyebrow-icon.svg'

function BlogArticleCardImage({
  hasCmsImage,
  image,
  imageAspectClassName = DEFAULT_IMAGE_ASPECT_CLASS_NAME,
  isExpired,
  title,
}: {
  hasCmsImage: boolean
  image: BlogPostData['heroImage']
  imageAspectClassName?: string
  isExpired?: boolean
  title: string
}) {
  const imageAlt =
    (typeof image === 'object' && image?.alt?.trim()) || title

  return (
    <div className="relative overflow-hidden rounded-[10px]">
      {hasCmsImage ? (
        <Media
          alt={imageAlt}
          className={cn(imageAspectClassName, 'w-full')}
          imgClassName={cn(
            'size-full object-cover transition-transform duration-300 group-hover:scale-[1.02]',
            isExpired && 'opacity-60 grayscale',
          )}
          resource={image}
          size="33vw"
        />
      ) : (
        <img
          alt={imageAlt}
          className={cn(
            imageAspectClassName,
            'w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]',
            isExpired && 'opacity-60 grayscale',
          )}
          height={360}
          src={DEFAULT_POST_HERO_IMAGE}
          width={640}
        />
      )}
      {isExpired ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[10px] bg-black/35"
        />
      ) : null}
    </div>
  )
}

function BlogArticleCardBadges({
  badgeStyle = 'blog',
  category,
  categoryColor,
  isExpired,
  readTime,
}: Pick<
  BlogArticleCardProps,
  'badgeStyle' | 'category' | 'categoryColor' | 'isExpired' | 'readTime'
>) {
  if (badgeStyle === 'event') {
    return (
      <div className="flex items-center justify-between gap-2">
        {category ? (
          <span className="inline-flex h-[1.625rem] items-center gap-0.5 rounded-full border border-black/10 bg-bluetext px-2.5 font-outfit text-base font-medium leading-5 text-white">
            <img
              alt=""
              aria-hidden
              className="size-4 shrink-0"
              height={16}
              src={EVENT_LOCATION_BADGE_ICON}
              width={16}
            />
            {category}
          </span>
        ) : (
          <span />
        )}
        <span
          className={cn(
            'inline-flex h-[1.625rem] items-center rounded-full border border-black/10 px-3 font-outfit font-medium leading-5 text-dark-800/80',
            isExpired
              ? 'bg-black/10 text-sm'
              : 'bg-brand-blue text-base',
          )}
        >
          {readTime}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2">
      {category ? (
        <span
          className="inline-flex h-[1.625rem] items-center rounded-full px-3 font-outfit text-base font-medium leading-5 text-dark-800/80"
          style={{ backgroundColor: categoryColor }}
        >
          {category}
        </span>
      ) : (
        <span />
      )}
      <span className="inline-flex items-center rounded-full border-[1.5px] border-black/10 bg-black/10 px-3 py-0.75 font-outfit text-sm font-medium leading-5 text-dark-800/80">
        {readTime}
      </span>
    </div>
  )
}

function BlogArticleCardText({
  description,
  title,
}: Pick<BlogArticleCardProps, 'description' | 'title'>) {
  return (
    <div className="flex flex-1 flex-col gap-4">
      <h3 className="font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-dark-800/80">
        {title}
      </h3>

      {description ? (
        <p className="font-outfit text-base leading-5 text-dark-800/80">{description}</p>
      ) : null}
    </div>
  )
}

function BlogArticleCardLink({
  children,
  className,
  href,
  isExternal = false,
  linkDisabled = false,
  opensInNewTabLabel,
}: {
  children: React.ReactNode
  className?: string
  href?: string
  isExternal?: boolean
  linkDisabled?: boolean
  opensInNewTabLabel?: string
}) {
  if (linkDisabled || !href) {
    return <div className={className}>{children}</div>
  }

  if (isExternal) {
    return (
      <a
        className={className}
        href={href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
        {opensInNewTabLabel ? (
          <span className="sr-only"> {opensInNewTabLabel}</span>
        ) : null}
      </a>
    )
  }

  return (
    <Link className={className} href={href}>
      {children}
    </Link>
  )
}

function BlogArticleCardContent({
  badgeStyle,
  category,
  categoryColor,
  ctaLabel,
  description,
  hasCmsImage,
  href,
  image,
  imageAspectClassName,
  isExpired,
  isExternal = false,
  linkDisabled = false,
  opensInNewTabLabel,
  readTime,
  title,
  variant,
}: BlogArticleCardProps & { hasCmsImage: boolean }) {
  const interactiveLinkClassName = cn(
    'group flex h-full flex-col overflow-hidden rounded-[22px] border-[1.5px] transition-[box-shadow,transform] duration-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
    variant === 'featured'
      ? 'border-white bg-white/95 focus-visible:ring-offset-bluetext'
      : 'border-black/10 bg-white/70 focus-visible:ring-offset-cream',
  )

  const staticCardClassName = cn(
    'flex h-full flex-col overflow-hidden rounded-[22px] border-[1.5px]',
    variant === 'featured'
      ? 'border-white bg-white/95'
      : 'border-black/10 bg-white/70',
  )

  const content = (
    <div className="flex flex-1 flex-col gap-4 p-3">
      <BlogArticleCardImage
        hasCmsImage={hasCmsImage}
        image={image}
        imageAspectClassName={imageAspectClassName}
        isExpired={isExpired}
        title={title}
      />
      <BlogArticleCardBadges
        badgeStyle={badgeStyle}
        category={category}
        categoryColor={categoryColor}
        isExpired={isExpired}
        readTime={readTime}
      />
      <BlogArticleCardText description={description} title={title} />
      {ctaLabel ? (
        <Button
          asChild
          className="h-12 w-full border-black/10"
          icon={<ArrowRight aria-hidden className="size-4" />}
        >
          <BlogArticleCardLink
            href={href}
            isExternal={isExternal}
            linkDisabled={linkDisabled}
            opensInNewTabLabel={opensInNewTabLabel}
          >
            {ctaLabel}
          </BlogArticleCardLink>
        </Button>
      ) : null}
    </div>
  )

  if (ctaLabel) {
    return content
  }

  return (
    <BlogArticleCardLink
      className={linkDisabled || !href ? staticCardClassName : interactiveLinkClassName}
      href={href}
      isExternal={isExternal}
      linkDisabled={linkDisabled}
      opensInNewTabLabel={opensInNewTabLabel}
    >
      {content}
    </BlogArticleCardLink>
  )
}

export function BlogArticleCard({
  badgeStyle = 'blog',
  category,
  categoryColor,
  className,
  ctaLabel,
  description,
  href,
  image,
  imageAspectClassName,
  isExpired,
  isExternal,
  linkDisabled,
  listItem = true,
  opensInNewTabLabel,
  readTime,
  title,
  variant = 'default',
}: BlogArticleCardProps) {
  const hasCmsImage = Boolean(image && typeof image === 'object')

  const article = (
    <article
      className={cn(
        'h-full',
        ctaLabel && [
          'flex w-full max-w-[32.5rem] flex-col gap-0 overflow-hidden rounded-[22px] border-[1.5px] border-white bg-white/95 shadow-[0_16px_32px_-8px_rgba(12,12,13,0.2)] lg:max-w-none',
          !listItem && className,
        ],
        !ctaLabel && !listItem && className,
      )}
    >
      <BlogArticleCardContent
        badgeStyle={badgeStyle}
        category={category}
        categoryColor={categoryColor}
        ctaLabel={ctaLabel}
        description={description}
        hasCmsImage={hasCmsImage}
        href={href}
        image={image}
        imageAspectClassName={imageAspectClassName}
        isExpired={isExpired}
        isExternal={isExternal}
        linkDisabled={linkDisabled}
        opensInNewTabLabel={opensInNewTabLabel}
        readTime={readTime}
        title={title}
        variant={variant}
      />
    </article>
  )

  if (!listItem) {
    return article
  }

  return <li className={cn('h-full', listItem && className)}>{article}</li>
}

export type GetBlogArticleCardPropsOptions = {
  colorByTitle?: Record<string, string>
  index?: number
}

export function getBlogArticleCardProps(
  post: BlogPostData,
  href: string,
  readTimeTemplate: string,
  options: GetBlogArticleCardPropsOptions = {},
): BlogArticleCardProps {
  const { colorByTitle = {}, index = 0 } = options

  return {
    category: getCategoryTitle(post.categories),
    categoryColor: getPostCardCategoryColor(post, { colorByTitle, index }),
    description: getPostCardDescription(post),
    href,
    image: getPostCardImage(post),
    readTime: getPostCardReadTime(post, readTimeTemplate),
    title: post.title,
  }
}
